/** The three stores documents, as declarations.
 *
 * Goods Receipt, Issue and Transfer differ in about a dozen fields and agree
 * on everything else: a Draft with lines, an authorise that posts to the
 * ledger, a cancel that does not. So they are one screen and three
 * declarations.
 *
 * That is ledger **D28** applied to our own code rather than only to GHERP's.
 * GHERP wrote nine goods-receipt forms, twelve gate passes and eight delivery
 * challans — and the ninth is always the one that misses a fix. Writing three
 * near-identical React screens here would be the same mistake at a smaller
 * scale.
 *
 * `STORES.md` is the design.
 */

export interface DocField {
  key: string
  label: string
  type?: 'text' | 'date' | 'select' | 'number'
  required?: boolean
  options?: { value: string; label: string }[]
  /** Fill from a master. `value`/`label` name the columns to read. */
  from?: { path: string; value: string; label: string; filter?: string }
  help?: string
  /** Money — hidden from a caller without `view_values`. */
  value?: boolean
}

export interface LineField extends DocField {
  width?: string
}

export interface StoresDoc {
  kind: 'grn' | 'issue' | 'transfer'
  title: string
  singular: string
  /** `stores.<object>.*` — the permission family. */
  object: string
  idKey: string
  /** What the movement does, said in the words of the person doing it. */
  posts: string
  header: DocField[]
  lines: LineField[]
  /** Which header field names the location stock is checked against, and
   *  which line field names the stage. Absent on a receipt: nothing is
   *  consumed, so there is nothing to check. */
  availability?: { locationField: string; stageField: string }
}

const TODAY = () => new Date().toISOString().slice(0, 10)
export const today = TODAY

export const GRN: StoresDoc = {
  kind: 'grn',
  title: 'Goods Receipts',
  singular: 'Goods Receipt',
  object: 'grn',
  idKey: 'grn_id',
  posts: 'Authorising adds this material to stock and moves the weighted '
       + 'average of every item on it.',
  header: [
    { key: 'doc_date', label: 'Date', type: 'date', required: true },
    // One screen, one field — GHERP wrote nine forms for these. Ledger D28.
    { key: 'receipt_type', label: 'Receipt Type', type: 'select', required: true,
      options: [
        { value: 'Purchase', label: 'Purchase — against an order, or direct' },
        { value: 'JobWorkReturn', label: 'Job Work Return — back from a subcontractor' },
        { value: 'CustomerSupplied', label: 'Customer Supplied — their material' },
        { value: 'Production', label: 'Production — made in-house, into stock' },
        { value: 'Return', label: 'Return — back from a project or customer' },
      ] },
    { key: 'supplier_id', label: 'Supplier', required: true,
      from: { path: 'masters/suppliers', value: 'supplier_code', label: 'name' } },
    { key: 'location_id', label: 'Received Into', required: true,
      from: { path: 'masters/locations', value: 'location_id', label: 'name' },
      help: 'Where the material physically lands' },
    { key: 'supplier_dc_no', label: 'Supplier Challan No' },
    { key: 'supplier_dc_date', label: 'Supplier Challan Date', type: 'date' },
    { key: 'vehicle_no', label: 'Vehicle No' },
    { key: 'eway_bill_no', label: 'E-Way Bill No' },
    { key: 'remarks', label: 'Remarks' },
  ],
  lines: [
    { key: 'item_id', label: 'Item', required: true,
      from: { path: 'masters/items', value: 'item_id', label: 'item_name' } },
    { key: 'qty', label: 'Quantity', type: 'number', required: true, width: '110px' },
    { key: 'rate', label: 'Rate', type: 'number', required: true, width: '110px',
      value: true },
    { key: 'valuation_class', label: 'Valuation', type: 'select', width: '150px',
      options: [
        { value: 'Valued', label: 'Valued' },
        { value: 'FreeOfCost', label: 'Free of cost' },
      ] },
    // Carried, not enforced. ITEM_MASTER.md §7 — traceability is built after
    // these screens, and the column exists now so that work needs no
    // migration over live movements.
    { key: 'heat_no', label: 'Heat No', width: '120px',
      help: 'Optional for now' },
  ],
  // No availability check: a receipt adds, it never consumes.
}

export const ISSUE: StoresDoc = {
  kind: 'issue',
  title: 'Material Issues',
  singular: 'Material Issue',
  object: 'issue',
  idKey: 'issue_id',
  posts: 'Authorising takes this material out of stock at the location’s '
       + 'current average. The average itself does not move.',
  header: [
    { key: 'doc_date', label: 'Date', type: 'date', required: true },
    { key: 'purpose', label: 'Purpose', type: 'select', required: true,
      options: [
        { value: 'Project', label: 'Project — consumed on a project' },
        { value: 'Production', label: 'Production — into a works order' },
        { value: 'Scrap', label: 'Scrap — written off' },
        { value: 'Sample', label: 'Sample' },
        { value: 'Other', label: 'Other' },
      ] },
    { key: 'location_id', label: 'Issued From', required: true,
      from: { path: 'masters/locations', value: 'location_id', label: 'name' } },
    { key: 'issued_to', label: 'Issued To' },
    { key: 'remarks', label: 'Remarks' },
  ],
  lines: [
    { key: 'item_id', label: 'Item', required: true,
      from: { path: 'masters/items', value: 'item_id', label: 'item_name' } },
    { key: 'stage', label: 'Stage', required: true, width: '170px',
      from: { path: 'masters/stages', value: 'stage_code', label: 'name' },
      help: 'Which state of the material' },
    { key: 'qty', label: 'Quantity', type: 'number', required: true, width: '110px' },
    { key: 'heat_no', label: 'Heat No', width: '120px' },
  ],
  availability: { locationField: 'location_id', stageField: 'stage' },
}

export const TRANSFER: StoresDoc = {
  kind: 'transfer',
  title: 'Stock Transfers',
  singular: 'Stock Transfer',
  object: 'transfer',
  idKey: 'transfer_id',
  posts: 'Authorising writes TWO ledger rows at one rate — out of the sending '
       + 'location, into the receiving one. Value moves; it is never created.',
  header: [
    { key: 'doc_date', label: 'Date', type: 'date', required: true },
    { key: 'from_location_id', label: 'From', required: true,
      from: { path: 'masters/locations', value: 'location_id', label: 'name' } },
    { key: 'to_location_id', label: 'To', required: true,
      from: { path: 'masters/locations', value: 'location_id', label: 'name' } },
    { key: 'remarks', label: 'Remarks' },
  ],
  lines: [
    { key: 'item_id', label: 'Item', required: true,
      from: { path: 'masters/items', value: 'item_id', label: 'item_name' } },
    { key: 'from_stage', label: 'From Stage', required: true, width: '160px',
      from: { path: 'masters/stages', value: 'stage_code', label: 'name' } },
    { key: 'to_stage', label: 'To Stage', required: true, width: '160px',
      from: { path: 'masters/stages', value: 'stage_code', label: 'name' },
      help: 'Usually the same' },
    { key: 'qty', label: 'Quantity', type: 'number', required: true, width: '110px' },
  ],
  availability: { locationField: 'from_location_id', stageField: 'from_stage' },
}

export const STORES_DOCS: Record<string, StoresDoc> = {
  'stores/grn': GRN,
  'stores/issue': ISSUE,
  'stores/transfer': TRANSFER,
}
